import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { fileUrl } from "../lib/api";
import { X } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const TIER_LABELS = {
  video:    { label: "Video Gifts",        accent: "text-papaya" },
  small:    { label: "Small Gifts",        accent: "text-cream/70" },
  music:    { label: "Music & Dance",      accent: "text-lignum" },
  funny:    { label: "Funny & Viral",      accent: "text-jam-gold" },
  culture:  { label: "Caribbean Culture",  accent: "text-jam-green" },
  premium:  { label: "Premium",            accent: "text-papaya" },
  vip:      { label: "VIP",                accent: "text-pink-300" },
  community:{ label: "Community",          accent: "text-emerald-300" },
  limited:  { label: "Limited Edition",    accent: "text-jam-gold" },
};
const TIER_ORDER = ["limited", "community", "video", "small", "music", "funny", "culture", "premium", "vip"];

export default function GiftModal({ recipient, post, onClose, onSent }) {
  const [catalog, setCatalog] = useState([]);
  const [waves, setWaves] = useState(0);
  const [activeTier, setActiveTier] = useState("video");
  const [tierPicked, setTierPicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const [{ data: g }, { data: w }] = await Promise.all([
        api.get("/gifts/catalog"), api.get("/wallet"),
      ]);
      setCatalog(g.gifts);
      setWaves(w.waves);
    } catch (err) {
      console.error("gift load failed", err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const groupedByTier = useMemo(() => {
    const groups = {};
    for (const g of catalog) {
      const t = g.tier || "small";
      (groups[t] ||= []).push(g);
    }
    return groups;
  }, [catalog]);

  const tiersAvailable = TIER_ORDER.filter((t) => groupedByTier[t]?.length);
  useEffect(() => {
    if (!tierPicked && tiersAvailable.length && !tiersAvailable.includes(activeTier)) setActiveTier(tiersAvailable[0]);
  }, [tiersAvailable, activeTier, tierPicked]);
  const activeGifts = groupedByTier[activeTier] || [];

  const send = async (gift) => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/gifts/send", {
        gift_id: gift.id,
        recipient_id: recipient.id,
        post_id: post?.id || null,
      });
      setWaves(data.waves);
      setMsg(`Sent ${gift.emoji} ${gift.label}! ${recipient.display_name || recipient.username} earned ${data.pearls_earned_by_creator} 🪩`);
      // Pass the full gift catalogue entry up so the parent can play the 3D burst + SFX
      onSent?.(gift);
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed";
      setMsg(typeof detail === "string" ? detail : "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-end" data-testid="gift-modal">
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-h-[88vh] crystal-glass rounded-t-3xl border-t border-x border-white/10 animate-fade-up flex flex-col">
        <div className="p-5 pb-3 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-cream text-2xl font-black tracking-tight">Send a gift</h3>
              <p className="text-cream/50 text-xs tracking-[0.2em] uppercase">to @{recipient.username}</p>
            </div>
            <button onClick={onClose} data-testid="gift-close" className="p-2 rounded-full hover:bg-white/5">
              <X size={22} className="text-cream" />
            </button>
          </div>

          <div className="flex items-center justify-between mb-3 px-4 py-3 rounded-2xl bg-white/5 border border-white/5">
            <span className="text-cream/60 text-sm">Your Coins</span>
            <span data-testid="gift-balance" className="text-cream font-black text-xl">{waves} 🪙</span>
          </div>

          {/* Tier tabs */}
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="gift-tiers">
            {tiersAvailable.map((t) => (
              <button
                key={t}
                data-testid={`gift-tier-${t}`}
                onClick={() => { setActiveTier(t); setTierPicked(true); }}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] tracking-[0.18em] uppercase font-black transition-colors ${activeTier === t ? "bg-papaya text-ink" : "bg-white/5 text-cream/60 hover:text-cream"}`}
              >
                {TIER_LABELS[t]?.label || t}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-5">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5" data-testid="gift-catalog">
            {activeGifts.map((g) => {
              const canAfford = waves >= g.waves;
              const isSig = g.is_signature;
              return (
                <button
                  key={g.id}
                  disabled={!canAfford || busy}
                  data-testid={`gift-${g.id}`}
                  onClick={() => send(g)}
                  className={`relative p-2.5 rounded-2xl flex flex-col items-center gap-1.5 transition-all duration-300 ${canAfford ? `bg-white/5 border ${isSig ? "border-jam-gold/60" : "border-white/10"} hover:border-papaya hover:-translate-y-1` : "bg-white/5 border border-white/5 opacity-40 cursor-not-allowed"}`}
                >
                  {isSig && (
                    <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 bg-jam-gold text-jam-black text-[8px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded-full">Iconic</span>
                  )}
                  {g.video_url && (
                    <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 bg-papaya text-ink text-[8px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded-full">▶ Video</span>
                  )}
                  {g.is_community && (
                    <span className={`absolute -top-1.5 left-1/2 -translate-x-1/2 text-[8px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded-full ${g.is_limited ? "bg-jam-gold text-jam-black" : "bg-emerald-400 text-ink"}`}>{g.badge || (g.is_limited ? "Limited" : "Community")}</span>
                  )}
                  {g.image_url ? (
                    <div className="w-12 h-12 rounded-xl overflow-hidden ring-1 ring-jam-gold/40" style={g.custom ? { background: `linear-gradient(135deg, ${g.custom.colors?.bg1}, ${g.custom.colors?.bg2})` } : undefined}>
                      <img src={fileUrl(g.image_url)} alt={g.label} className="w-full h-full object-contain" loading="lazy" />
                    </div>
                  ) : g.custom ? (
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl" style={{ background: `linear-gradient(135deg, ${g.custom.colors?.bg1}, ${g.custom.colors?.bg2})` }}>{g.emoji}</div>
                  ) : (
                    <span className="text-3xl leading-none">{g.emoji}</span>
                  )}
                  <span className="text-cream font-bold text-[11px] leading-tight text-center">{g.label}</span>
                  <span className="text-papaya font-black text-[10px] tracking-widest">{g.waves} 🪙</span>
                </button>
              );
            })}
          </div>

          {msg && <p data-testid="gift-msg" className="mt-4 text-sm text-cream/80 text-center">{msg}</p>}

          <Link
            to="/wallet"
            data-testid="gift-buy-waves"
            className="mt-5 block text-center text-papaya text-xs font-black tracking-[0.2em] uppercase hover:text-papaya-hover transition-colors"
          >
            Need more Coins? Top up →
          </Link>
        </div>
      </div>
    </div>
  );
}
